import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { Button, Grid2, Typography } from '@mui/material';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import CustomDivider from '../../shared/utils/CustomDivider';
import { Grid_Gap } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import useAuthStore from '../../store/useAuthStore';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { useGetTemplate } from '../../assets/api/useAdminApi';
import { useState } from 'react';
import api from '../../assets/nodeApi/axiosInstance';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';

const TemplateForm = () => {
    const { token } = useAuthStore()
    const { showToaster } = useToaster();
    const [filters, setFilters] = useState({});
    const { data: templateList, isLoading: isTemplateLoading } = useGetTemplate()
    const queryClient = useQueryClient();

    const templateColumns = [
        {
            field: "sr_no",
            headerName: "Sr No",
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>

        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "category",
            headerName: "Category",
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "text",
            headerName: "Text",
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.row?.
                components?.[0]?.text}</Typography>,
        },

        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        }
    ];


    const validationSchema = Yup.object().shape({
        category: Yup.string().required("Template Type is required"),
        name: Yup.string()
            .required("Template Name is required")
            .matches(/^[a-z_]+$/, "Only lowercase letters and underscores are allowed"),
        text: Yup.string().required("Content is required"),
    });

    const initialValues = {
        category: "",
        name: "",
        text: "",
    };


    const mutation = useMutation({
        mutationFn: (formData) => api.post("create-templates", formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        ),
    });

    const handleSubmit = (values, { setSubmitting, resetForm, }) => {
        mutation.mutate(values, {
            onSuccess: (response) => {

                showToaster('Success', response?.data?.message, 'success')

                queryClient.invalidateQueries({ queryKey: ['fetch-templates'] });

                resetForm()
            },
            onError: (error) => {
                if (!error?.response?.data?.success) {
                    showToaster("", error?.response?.data?.message, 'error')
                } else {
                    showToaster('Failed to Create Template', "Failed to Create Template", 'error')
                }

            },
            onSettled: () => {
                setSubmitting(false);
            },
        });
    }

    const templateType = [
        {
            id: 1, value: "MARKETING", label: "MARKETING"
        },
        {
            id: 2, value: "UTILITY", label: "UTILITY"
        }
    ]

    return (
        <>
        <NormalContainerWithHeader headerText={"Add Template"}>
         <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ resetForm, }) => (
                    <Form>
                        <Grid2 container spacing={Grid_Gap}>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="category"
                                    component={AutoCompleteField}
                                    options={templateType}
                                    label="Template Type"
                                    isRequired={true}


                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="name"
                                    component={TextFormField}
                                    label="Template Name"
                                    isRequired={true}
                                    type={"name"}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="text"
                                    component={TextFormField}
                                    label="Write Your Content Here"
                                    isRequired={true}
                                    multiline
                                    rows={3}

                                />
                            </Grid2>
                        </Grid2>
                        <CustomDivider />
                        <Grid2 container spacing={Grid_Gap} sx={{ display: "flex", justifyContent: "end" }}>
                            <Grid2 children size={{ xs: 12, sm: 3 }}>
                                <Button variant='outlined' fullWidth onClick={resetForm}>Cancel</Button>
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 3 }}>
                                <Button variant='contained' type="submit" fullWidth>Submit</Button>
                            </Grid2>
                        </Grid2>

                    </Form>
                )}
            </Formik>    
        </NormalContainerWithHeader>
           
            <CustomDivider />
            <TableFilterTemplate
                title="Template List"
                columns={templateColumns}
                rowsData={templateList?.api_response?.data || []}
                isLoading={isTemplateLoading}
                filterInitialValues={filters}
                filterReset={() => setFilters({})}
                onApplyFilters={(f) => setFilters(f)}
            />
        </>
    )
}

export default TemplateForm